// src/eshop/hooks/useGuestOtp.ts (actualizado)
import { useCallback, useEffect, useRef, useState } from 'react';
import { connsPublic } from '@cfg/axiosConfig.tsx';
import { normalizeEmail, normalizePhone } from '@eshop/utils/contactNormalization.ts';
import { isAxiosError } from 'axios';

type Step1Response = {
  authToken?: string;
  error?: string;
  actionCode?: string;
  userExists?: boolean;
};

type Step2Response = {
  authToken?: string;               // presente si hubo rotación por error
  error?: string;                   // "Código incorrecto" (back reenvía nuevo código)
  guest_verified_at?: string;       // éxito en guest_checkout
  user?: unknown;                   // éxito en login/registro (no usado aquí)
};

type RequestResult = { success: true; userExists?: boolean } | { success: false; error: string; actionCode?: string };
type VerifyResult = { success: true; verifiedAt?: string } | { success: false; error: string };

const WS_COOLDOWN_SECONDS = 60;

export function useGuestOtp() {
  const [status, setStatus] = useState<'idle' | 'requesting' | 'verifying'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [cooldown, setCooldown] = useState<number>(0);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState<string | undefined>();
  const [authToken, setAuthToken] = useState<string | undefined>();
  const [userExists, setUserExists] = useState<boolean | undefined>();
  const [fallbackToken, setFallbackToken] = useState<string>();
  const captureDeliveryFailure = useCallback((cause: unknown) => {
    const detail = isAxiosError(cause) ? cause.response?.data?.detail : undefined;
    if (detail?.code !== 'otp_delivery_failed' || !detail.fallback_available || typeof detail.fallback_token !== 'string') return false;
    setFallbackToken(detail.fallback_token);
    setIsCodeSent(false);
    setError('No pudimos enviar el código de WhatsApp. Podés continuar con el contacto pendiente de revisión.');
    return true;
  }, []);

  const timerRef = useRef<number | null>(null);
  const lastContactRef = useRef<{ tel: string; email: string } | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearTimer();
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    clearTimer();
    setCooldown(seconds);
    timerRef.current = window.setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          clearTimer();
          return 0;
        }
        return s - 1;
      });
    }, 1000) as unknown as number;
  }, []);

  const requestCode = useCallback(
    async (rawPhone: string, rawEmail: string): Promise<RequestResult> => {
      setError(undefined);
      setVerifiedAt(undefined);
      setFallbackToken(undefined);

      const tel = normalizePhone(rawPhone) || '';
      const email = normalizeEmail(rawEmail) || '';

      if (!tel || !email) {
        return { success: false, error: 'Ingresá email y teléfono válidos.' };
      }

      setStatus('requesting');
      try {
        const payload = {
          method: 'wsp',
          mode: 'login',
          context: 'guest_checkout',
          tel,
          email,
        };
        const { data } = await connsPublic<Step1Response>('auth:code').post(payload);
        if (data.error) {
          setStatus('idle');
          setIsCodeSent(false);
          return { success: false, error: data.error, actionCode: data.actionCode };
        }
        if (!data.authToken) {
          setStatus('idle');
          setIsCodeSent(false);
          return { success: false, error: 'No se pudo generar el token de verificación.' };
        }
        // éxito: guardar token, recordar contacto y arrancar cooldown de WhatsApp (60s)
        setAuthToken(data.authToken);
        lastContactRef.current = { tel, email };
        setIsCodeSent(true);
        setUserExists(data.userExists);
        startCooldown(WS_COOLDOWN_SECONDS);
        setStatus('idle');
        return { success: true, userExists: data.userExists };
      } catch (e) {
        setStatus('idle');
        setIsCodeSent(false);
        const detail = isAxiosError(e) ? e.response?.data?.detail : undefined;
        if (captureDeliveryFailure(e)) {
          return { success: false, error: 'No pudimos enviar el código de WhatsApp.' };
        }
        const errorMessage = detail?.code === 'invalid_phone' ? 'Revisá el número de WhatsApp.' : 'No se pudo enviar el código. Probá de nuevo.';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [startCooldown, captureDeliveryFailure],
  );

  const verifyCode = useCallback(
    async (code: string): Promise<VerifyResult> => {
      setError(undefined);
      if (!authToken) {
        return { success: false, error: 'Token de verificación ausente. Solicitá un nuevo código.' };
      }
      setStatus('verifying');
      try {
        const { data } = await connsPublic<Step2Response>('auth:code').post({ authToken, code });
        // Caso 1: error -> el back rotó token y reenvió código (¡actualizar token y reiniciar cooldown!)
        if (data.error) {
          if (data.authToken) setAuthToken(data.authToken);
          // nuevo código reenviado por el back
          startCooldown(WS_COOLDOWN_SECONDS);
          setIsCodeSent(true);
          setStatus('idle');
          setError('Código incorrecto. Te enviamos un nuevo código.');
          return { success: false, error: 'Código incorrecto.' };
        }
        // Caso 2: verificación guest OK
        if (data.guest_verified_at) {
          setVerifiedAt(data.guest_verified_at);
          setStatus('idle');
          return { success: true, verifiedAt: data.guest_verified_at };
        }
        // Caso 3: (si fuera login/registro normal)
        if (data.user) {
          setStatus('idle');
          return { success: true };
        }
        // Fallback
        setStatus('idle');
        return { success: false, error: 'Respuesta inesperada. Solicitá un nuevo código.' };
      } catch (e) {
        setStatus('idle');
        if (captureDeliveryFailure(e)) return { success: false, error: 'No pudimos reenviar el código. Podés continuar sin verificar.' };
        return { success: false, error: 'No pudimos validar el código. Intentá nuevamente.' };
      }
    },
    [authToken, startCooldown, captureDeliveryFailure],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setError(undefined);
    setCooldown(0);
    setIsCodeSent(false);
    setVerifiedAt(undefined);
    setAuthToken(undefined);
    setUserExists(undefined);
    setFallbackToken(undefined);
    lastContactRef.current = null;
    clearTimer();
  }, []);

  return {
    // estado
    status,
    error,
    cooldown,
    canResend: cooldown === 0,
    isRequesting: status === 'requesting',
    isVerifying: status === 'verifying',
    isCodeSent,
    verifiedAt,
    userExists,
    fallbackToken,

    // acciones
    requestCode,
    verifyCode,
    reset,
  };
}
